import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Tracker, TrackerEntry } from "../../types";
import { CreateTrackerResponse, UpdateTrackerEntryResponse } from "../../types/responses";
import { generateUUID } from "../../utils/uuidUtils";
import { API_ENDPOINTS, authenticatedFetch } from "../../config/api";

interface TrackersState {
  items: Tracker[];
  entries: TrackerEntry[];
  loading: boolean;
  error: string | null;
}

// Async thunk for fetching trackers via API
const fetchTrackers = createAsyncThunk(
  "trackers/fetchTrackers",
  async (
    {
      startDate,
      endDate,
      isActive,
      withEntries,
    }: {
      startDate: string;
      endDate: string;
      isActive?: boolean;
      withEntries?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
        ...(isActive !== undefined && { isActive: isActive.toString() }),
      });
      const url = withEntries
        ? `${API_ENDPOINTS.TRACKERS.GET_WITH_ENTRIES}?${params.toString()}`
        : `${API_ENDPOINTS.TRACKERS.GET_ALL}?${params.toString()}`;

      const response = await authenticatedFetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch trackers: ${response.statusText}`);
      }

      const data = await response.json();

      if (withEntries) {
        // Response is an array of trackers with entries embedded
        const trackersWithEntries: Array<
          Tracker & { entries: TrackerEntry[] }
        > = data;
        // Extract trackers (remove entries property)
        const trackers: Tracker[] = trackersWithEntries.map(
          ({ entries, ...tracker }) => tracker
        );
        // Flatten all entries from all trackers
        const entries: TrackerEntry[] = trackersWithEntries.flatMap(
          (tracker) => tracker.entries || []
        );
        return { trackers, entries };
      } else {
        // Response is just trackers
        const trackers: Tracker[] = data;
        return { trackers, entries: [] };
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for fetching tracker with entries via API
const fetchTrackerWithEntries = createAsyncThunk(
  "trackers/fetchTrackerWithEntries",
  async (
    {
      trackerId,
      startDate,
      endDate,
    }: { trackerId: string; startDate: string; endDate: string },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
      });
      const url = `${API_ENDPOINTS.TRACKERS.GET_TRACKER_WITH_ENTRIES(
        trackerId
      )}?${params.toString()}`;

      const response = await authenticatedFetch(url);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch tracker with entries: ${response.statusText}`
        );
      }

      const data = await response.json();
      return data; // Should return { tracker: Tracker, entries: TrackerEntry[] }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for fetching tracker entries via API
const fetchTrackerEntries = createAsyncThunk(
  "trackers/fetchTrackerEntries",
  async (
    {
      startDate,
      endDate,
      trackerId,
    }: { startDate: string; endDate: string; trackerId: string },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
      });
      const url = `${API_ENDPOINTS.TRACKERS.GET_ENTRIES_BY_TRACKER(
        trackerId
      )}?${params.toString()}`;

      const response = await authenticatedFetch(url);

      if (!response.ok) {
        throw new Error(
          `Failed to fetch tracker entries: ${response.statusText}`
        );
      }

      const entries: TrackerEntry[] = await response.json();
      return entries;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for creating a tracker via API
const createTracker = createAsyncThunk(
  "trackers/createTracker",
  async (
    trackerData: Omit<Tracker, "id" | "userId" | "createdAt" | "streak" | "bestStreak">,
    { rejectWithValue }
  ) => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.TRACKERS.CREATE, {
        method: "POST",
        body: JSON.stringify(trackerData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create tracker: ${response.statusText}`);
      }

      const result: CreateTrackerResponse = await response.json();
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for updating a tracker via API
const updateTrackerAsync = createAsyncThunk(
  "trackers/updateTrackerAsync",
  async (
    {
      id,
      updates,
    }: {
      id: string;
      updates: Partial<Omit<Tracker, "id" | "userId" | "created">>;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINTS.TRACKERS.GET_ALL}/${id}`,
        {
          method: "PUT",
          body: JSON.stringify(updates),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update tracker: ${response.statusText}`);
      }

      const tracker: Tracker = await response.json();
      return tracker;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for deleting a tracker via API
const deleteTrackerAsync = createAsyncThunk(
  "trackers/deleteTracker",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINTS.TRACKERS.GET_ALL}/${id}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete tracker: ${response.statusText}`);
      }

      return id;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

// Async thunk for creating/updating tracker entry via API
const createTrackerEntry = createAsyncThunk(
  "trackers/createTrackerEntry",
  async (
    {
      trackerId,
      date,
      value,
    }: { trackerId: string; date: string; value: string | number | boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINTS.TRACKERS.GET_ALL}/${trackerId}/entries`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            date,
            value,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to create tracker entry: ${response.statusText}`
        );
      }

      const result: UpdateTrackerEntryResponse = await response.json();
      return result;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error"
      );
    }
  }
);

const initialState: TrackersState = {
  items: [],
  entries: [],
  loading: false,
  error: null,
};

const trackersSlice = createSlice({
  name: "trackers",
  initialState,
  reducers: {
    addTracker: (
      state,
      action: PayloadAction<Omit<Tracker, "id" | "createdAt">>
    ) => {
      const newTracker: Tracker = {
        ...action.payload,
        id: generateUUID(),
        createdAt: new Date().toISOString(),
      };
      state.items.push(newTracker);
    },
    updateTracker: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<Tracker> }>
    ) => {
      const { id, updates } = action.payload;
      const trackerIndex = state.items.findIndex(
        (tracker) => tracker.id === id
      );
      if (trackerIndex !== -1) {
        state.items[trackerIndex] = {
          ...state.items[trackerIndex],
          ...updates,
        };
      }
    },
    deleteTracker: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(
        (tracker) => tracker.id !== action.payload
      );
      state.entries = state.entries.filter(
        (entry) => entry.trackerId !== action.payload
      );
    },
    addTrackerEntry: (
      state,
      action: PayloadAction<
        Omit<TrackerEntry, "id" | "createdAt" | "updatedAt">
      >
    ) => {
      const newEntry: TrackerEntry = {
        ...action.payload,
        id: generateUUID(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      state.entries.push(newEntry);
    },
    updateTrackerEntry: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<TrackerEntry> }>
    ) => {
      const { id, updates } = action.payload;
      const entryIndex = state.entries.findIndex((entry) => entry.id === id);
      if (entryIndex !== -1) {
        state.entries[entryIndex] = {
          ...state.entries[entryIndex],
          ...updates,
          updatedAt: new Date().toISOString(),
        };
      }
    },
    deleteTrackerEntry: (state, action: PayloadAction<string>) => {
      state.entries = state.entries.filter(
        (entry) => entry.id !== action.payload
      );
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearTrackers: (state) => {
      state.items = [];
      state.entries = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrackers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrackers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.trackers;
        // Merge entries if provided
        if (action.payload.entries && action.payload.entries.length > 0) {
          // Clear existing entries and add new ones
          state.entries = action.payload.entries;
        }
      })
      .addCase(fetchTrackers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTrackerWithEntries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrackerWithEntries.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;

        // API response structure: Tracker object with embedded entries array
        // Extract entries from the response
        const { entries, ...trackerData } = data;

        // Create the tracker object (remove entries property)
        const tracker: Tracker = trackerData;

        // Update or add the tracker
        if (tracker && tracker.id) {
          const existingTrackerIndex = state.items.findIndex(
            (t) => t.id === tracker.id
          );
          if (existingTrackerIndex !== -1) {
            state.items[existingTrackerIndex] = tracker;
          } else {
            state.items.push(tracker);
          }
        }

        // Update entries for this tracker
        if (entries && Array.isArray(entries)) {
          state.entries = entries;
        } else {
          // If no entries, clear entries for this tracker
          state.entries = state.entries.filter(
            (entry) => entry.trackerId !== tracker.id
          );
        }
      })
      .addCase(fetchTrackerWithEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchTrackerEntries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTrackerEntries.fulfilled, (state, action) => {
        state.loading = false;
        state.entries = action.payload;
      })
      .addCase(fetchTrackerEntries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createTracker.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTracker.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(createTracker.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createTrackerEntry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTrackerEntry.fulfilled, (state, action) => {
        state.loading = false;
        const newEntry = action.payload;
        const tracker = state.items.find(t => t.id === newEntry.trackerId);
        if (tracker?.dataType === 'number') {
          // Allow multiple entries for number trackers
          state.entries.push(newEntry);
        } else {
          // Replace for other types
          const existingIndex = state.entries.findIndex(
            (entry) =>
              entry.trackerId === newEntry.trackerId &&
              entry.date === newEntry.date
          );
          if (existingIndex !== -1) {
            state.entries[existingIndex] = newEntry;
          } else {
            state.entries.push(newEntry);
          }
        }
      })
      .addCase(createTrackerEntry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateTrackerAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTrackerAsync.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTracker = action.payload;
        const index = state.items.findIndex(
          (tracker) => tracker.id === updatedTracker.id
        );
        if (index !== -1) {
          state.items[index] = updatedTracker;
        }
      })
      .addCase(updateTrackerAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteTrackerAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTrackerAsync.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.payload;
        state.items = state.items.filter((tracker) => tracker.id !== deletedId);
        state.entries = state.entries.filter(
          (entry) => entry.trackerId !== deletedId
        );
      })
      .addCase(deleteTrackerAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  addTracker,
  updateTracker,
  deleteTracker,
  addTrackerEntry,
  updateTrackerEntry,
  deleteTrackerEntry,
  setLoading,
  setError,
  clearTrackers,
} = trackersSlice.actions;
export {
  fetchTrackers,
  fetchTrackerWithEntries,
  fetchTrackerEntries,
  createTracker,
  createTrackerEntry,
  updateTrackerAsync,
  deleteTrackerAsync,
};
export default trackersSlice.reducer;
